import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridColDef, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { Button, FormControl, InputLabel } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ViewDownloadsDialog } from "../../dialogs/View-Downloads-Dialog";
import { UserPermissionDialog } from "../../dialogs/User-Permission-Dialog";
import { useAuth } from "../../../hooks/useAuth";
import { useRecoilState } from "recoil";
import { tokenState } from "../../../atoms/token";
import { userState } from "../../../atoms/user";
import { User } from "../../../models/user";
import axios from "../../../services/axiosInstance";
import { CacheService } from "../../../common/services/cache.service";
import environment from "../../../environment/environment";
import APIService from "../../../services/api.service";
import SearchBar from "../../app/SearchBar/SearchBar";
import './style.scss';
import { Helmet } from "react-helmet";

const roleMap = {
  "Technical Information Center": "etic_admin",
  "Sensitive": "etic_sensitive_view",
  "Archaeology": "etic_archaeology_view",
};
const reverseRoleMap = Object.fromEntries(Object.entries(roleMap).map(([k, v]) => [v, k]));

const Admin = () => {
  const { user } = useAuth();
  const [userInfo, setuserInfo] = useRecoilState(userState);  
  const [token] = useRecoilState(tokenState);
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [resultCount, setResultCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

  const [checkedItems, setCheckedItems] = useState({
    etic_admin: false,
    etic_sensitive_view: false,
    etic_archaeology_view: false,
  });
  
  const search_role_options = Object.keys(checkedItems);

  // Handling checkbox changes
  const handleRoleChange = (event) => {
    const { name, checked } = event.target;
    setCheckedItems({
      ...checkedItems,
      [name]: checked,
    });
  };
  
//  useEffect(() => {
//    search();
//  }, []);

  useEffect(() => {
    search();
  }, [paginationModel, sortModel, checkedItems]);


  const updateCurrentUserRoles = async () => {
    if (environment.publicMode) {
      return;
    }
    
    const url = `${environment.npgApiUrl}user/current`;
    try {
      const res = await axios.get(url, { headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token') }, withCredentials: true });
      setuserInfo(res.data);
    } catch (err) {
      console.error('Failed to update current user roles', err);
    }
  };

  const handleSaveClick = async (userRoles: string[], userId: string) => {
    const currentRoles = userRoles.map(role => roleMap[role]);
    const missingRoles = Object.keys(roleMap).filter(r => !userRoles.includes(r)).map(r => roleMap[r]);

    const addRequests = currentRoles.map(role => {
      const url = `${environment.npgApiUrl}user/adduserrole?userId=${userId}&role=${role}`;
      return new APIService<User>(axios, new CacheService, url, token).add(null, url);
    });

    const removeRequests = missingRoles.map(role => {
      const url = `${environment.npgApiUrl}user/removeuserrole?userId=${userId}&role=${role}`;
      return new APIService<User>(axios, new CacheService, url, token).add(null, url);
    });

    await Promise.all([...addRequests, ...removeRequests]);

    if (userId === userInfo.userId) {
      updateCurrentUserRoles();
    }

    setShowDialog(false);
    setSelectedId('');
    search();
  };

  const search = async (text = filterText) => {
    setLoading(true);

    let queryParts = [];

    if (filterText && filterText !== '') {
      queryParts.push(`filterValue=${encodeURIComponent(text)}`);  
    }

    if (sortModel.length && Object.keys(sortModel[0]).length > 0 && sortModel[0]?.field && sortModel[0].field !== 'action') {
      queryParts.push(`sortField=${sortModel[0].field}`);
      queryParts.push(`sortDirection=${sortModel[0].sort === 'desc' ? 'desc' : 'asc'}`);
    }

    queryParts.push(`page=${paginationModel.page + 1}&pageSize=${paginationModel.pageSize}`);

    search_role_options.forEach((value: string) => {
      if (checkedItems[value]) {
        queryParts.push(`role=${value}`);
      }
    });

    const url = `${environment.npgApiUrl}etic/getusers?${queryParts.join('&')}`;

    try 
    {
      await new APIService<User[]>(axios, new CacheService, url, token)
              .getAnyAsync<User[]>()
              .then((res:any) => {
          setResultCount(res.totalCount)
          console.log('ax', res);
          setUsers(res.data);
      });
    } 
    catch (err) 
    {
      console.error('Failed to load users', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const RoleAlias = (roles: string[] = []) => {
    return roles.map(role => reverseRoleMap[role]).filter(Boolean).join(', ');
  };

  const handleRowBtnClick = (event, rowParams) => {
    setSelectedId(rowParams.row.userId);
    setTimeout(() => setShowDialog(true), 0);
  };

  return (
    <>
      {showDialog && (
        <UserPermissionDialog
          userId={selectedId}
          handleSaveClick={handleSaveClick}
          handleCloseClick={() => 
            { 
              setShowDialog(false);
              setSelectedId(''); 
            }}
        />
      )}

      <h1 className="center">View All Users</h1>

      <SearchBar
        placeholder="Search for User..."
        filterText={filterText}
        onFilterTextChange={setFilterText}
        onSearchClick={() => search()}
        handleKeyUp={(e) => { //misnomer - only fires on enter key
        const value = e.value || e.target?.value || '';
          setFilterText(value);
          search(value);
        }}
      />

        <div className="center" style={{ width: '100%', flexDirection: "row" }}>
        {search_role_options.map((option) => (
          <label key={option} style={{ paddingLeft: 30, display: "inline" }}>
            <input style= {{ display: "inline" }}
              type="checkbox"
              name={option}
              checked={checkedItems[option]}
              onChange={handleRoleChange}
            />
            {reverseRoleMap[option]}
          </label>
        ))}
        </div>

      <Helmet>
        <meta charSet="utf-8" />
        <title>Users Page</title>
      </Helmet>

      <div className="search-results">
        <div className="center" style={{ height: 500, width: '100%' }}>
          <DataGrid
            sx={{
              '.MuiDataGrid-columnHeaderTitle': { fontWeight: 'bolder' },
              '.MuiDataGrid-columnHeaderTitleContainer': { borderBottom: 1 },
              '.MuiDataGrid-iconButtonContainer': { visibility: 'visible' },
              '.MuiDataGrid-sortIcon': { opacity: 'inherit !important' },
            }}
            rows={users.map(user => ({
              id: user.userId, // required by DataGrid
              userId: user.userId, // needed for editing
              name: user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : user?.name ? `${user?.name}` : user.userId,
              email: user.email,
              permissions: RoleAlias(user.roles),
            }))}
            columns={[
              { field: 'name', headerName: 'Name', width: 400, filterable: false },
              { field: 'email', headerName: 'Email', width: 400, filterable: false },
              { field: 'permissions', headerName: 'User Permissions', width: 400, filterable: false, sortable: false },
              {
                field: 'edit',
                headerName: 'Edit',
                hideSortIcons: true,
                width: 200,
                filterable: false,
                sortable: false,
                renderCell: (cellValues) => (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleRowBtnClick(event, cellValues);
                    }}
                    startIcon={<FontAwesomeIcon icon={["fas", "edit"]} />}
                  >
                    Permissions
                  </Button>
                ),
              },
            ]}
            getRowId={(row) => row.userId} // ensures unique row identity
            loading={loading}
            rowCount={resultCount}
            paginationModel={paginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            sortingMode="server"
            //filterMode="server"
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={setSortModel}
          />
        </div>
      </div>
    </>
  );
};

export default Admin;
